import {
    applicationRepository,
    eventRepository,
    seasonRepository,
    teamRepository
} from "../../storage/storage";
import mapSeason from "../../object-mappers/season-mapper";
import BaseDataStrategy from "./base-data-strategy";
import { mergeCollectionsById } from "../common/object-util";
import { sanitizeInvalidCharactersDeep } from "../common/string-util";
import ApplicationConfig from "../../sports/common/application-config";
import { mapPlayer, mapPlayerDto } from "../../object-mappers/player-mapper";
import { findPlayer } from "../../sports/common/utils/player-utils";
import { isReadOnly } from "../../sports/common/api-access";

class RemoteDataStrategy extends BaseDataStrategy {
    constructor() {
        super();
        this.gamedayApiClient = null;
        this.stretchLiveApiClient = null;
    }

    setStretchLiveApiClient(stretchLiveApiClient) {
        this.stretchLiveApiClient = stretchLiveApiClient;
    }

    setGamedayApiClient(gamedayApiClient) {
        this.gamedayApiClient = gamedayApiClient;
    }

    async getEventByExternalId(eventId) {
        let event = await this.gamedayApiClient.getEventByExternalId(eventId);
        if (event) {
            const [mappedEvents] = await this.mapEventsWithTeams([event]);
            if (mappedEvents) {
                await eventRepository.put(mappedEvents);
                event = mappedEvents;
            }
        }

        return event;
    }

    async mergeEvent(remoteEvent, mergeOptions) {
        const mergedEvent = { ...remoteEvent };
        if (mergeOptions) {
            const keepLocalValuesForProps = Object.entries(mergeOptions)
                .filter(([, v]) => v === "local")
                .map(([k]) => k);
            if (keepLocalValuesForProps.length) {
                const { eventId } = remoteEvent;
                const localEvent = await eventRepository
                    .where({ eventId })
                    .first();
                if (localEvent) {
                    keepLocalValuesForProps.forEach((key) => {
                        Object.assign(mergedEvent, { [key]: localEvent[key] });
                    });
                }
            }
        }

        return mergedEvent;
    }

    async mapEventsWithTeams(jsonEvents, mergeOptions) {
        const mappedEvents = [];
        for (const jsonEvent of jsonEvents) {
            const mergedEvent = await this.mergeEvent(jsonEvent, mergeOptions);
            const mappedEvent = await super.saveOrUpdateEvent(mergedEvent);

            mappedEvents.push(mappedEvent);
        }

        return mappedEvents.sort((a, b) => {
            return (
                b.statusCode - a.statusCode || a.startDateTime - b.startDateTime
            );
        });
    }

    async getSeasons(sportCodes = []) {
        const data = await this.gamedayApiClient.getSeasons(sportCodes);
        const localSeasons = await seasonRepository.getAll().toArray();

        const localSettingsMap = new Map();
        localSeasons.forEach((season) => {
            localSettingsMap.set(season.seasonId, {
                ruleSetId: season.ruleSetId,
                gameConfiguration: season.gameConfiguration,
                settingsUpdatedAt: season.settingsUpdatedAt
            });
        });

        const remoteSeasons = data.map(mapSeason);

        const mergedSeasons = mergeCollectionsById(
            localSeasons,
            remoteSeasons,
            "seasonId"
        );

        mergedSeasons.forEach((season) => {
            const localSettings = localSettingsMap.get(season.seasonId);
            if (localSettings) {
                this._preserveLocalSeasonSettings(season, localSettings);
            }
        });

        await seasonRepository.bulkPut(mergedSeasons);

        return super.getSeasons(sportCodes);
    }

    async getSeason(externalSeasonId) {
        const data = await this.gamedayApiClient.getSeason(externalSeasonId);
        const mapped = mapSeason(data);
        //
        const local = await seasonRepository
            .where({ seasonId: externalSeasonId })
            .first();
        if (local) {
            this._preserveLocalSeasonSettings(mapped, local);
        }

        await seasonRepository.put(mapped);

        return super.getSeason(externalSeasonId);
    }

    async getEventsByCriteria(
        seasonId,
        mergeOptions = {},
        filterOptions = {},
        pagingOptions = {}
    ) {
        const { localSearch } = mergeOptions;
        if (localSearch) {
            return super.getEventsByCriteria(
                seasonId,
                filterOptions,
                pagingOptions
            );
        }
        const season = await seasonRepository.getById(seasonId);
        const events = await this.gamedayApiClient.getEventsByCriteria(
            season.seasonId,
            filterOptions,
            pagingOptions
        );

        return {
            ...events,
            data: await this.mapEventsWithTeams(events.data, mergeOptions)
        };
    }

    async getLastWeekEvents(sportCodes = [], mergeOptions) {
        const data = await this.gamedayApiClient.getLastWeekEvents(sportCodes);

        return this.mapEventsWithTeams(data, mergeOptions);
    }

    async getNextWeekEvents(sportCodes = [], mergeOptions) {
        const data = await this.gamedayApiClient.getNextWeekEvents(sportCodes);
        return this.mapEventsWithTeams(data, mergeOptions);
    }

    async getOrCreateSeason(jsonSeason) {
        let season = await seasonRepository.getById(jsonSeason.seasonId);

        if (!season) {
            const data = await this.gamedayApiClient.getSeason(
                jsonSeason.seasonId
            );
            if (data) {
                let seasonId = await seasonRepository.put(mapSeason(data));
                season = await seasonRepository.getById(seasonId);
            }
        }
        return season;
    }

    async saveOrUpdatePlayer(player) {
        const team = await teamRepository.getById(player.teamId);
        const { apiAccess } = team;
        const disableSync = isReadOnly(apiAccess);

        let toSave = player;
        if (!disableSync) {
            const remotePlayer = await this.gamedayApiClient.saveOrUpdatePlayer(
                mapPlayerDto(player),
                team.teamId
            );
            Object.assign(toSave, remotePlayer);
        }

        return super.saveOrUpdatePlayer(mapPlayer(toSave, team.id));
    }

    async saveOrUpdatePlayers(remoteTeamId, players = []) {
        if (players) {
            const localTeam = await teamRepository
                .where({ teamId: remoteTeamId })
                .first();
            const { apiAccess } = localTeam;
            const disableSync = isReadOnly(apiAccess);

            let toSave = players;

            if (!disableSync) {
                const remotes = await this.gamedayApiClient.saveOrUpdatePlayers(
                    remoteTeamId,
                    players.map(mapPlayerDto)
                );
                toSave = players.map((p) =>
                    Object.assign(p, findPlayer(remotes, p))
                );
            }

            return super.saveOrUpdatePlayers(
                localTeam.id,
                toSave.map((p) => mapPlayer(p, localTeam.id))
            );
        }

        return [];
    }

    async syncTeamPlayers(team, externalEventId) {
        const remotePlayers = await this.gamedayApiClient.getEventPlayers(
            team.teamId,
            externalEventId
        );

        const localTeam = await this.saveOrUpdateTeam(team);
        const toSave = remotePlayers.map((p) => mapPlayer(p, localTeam.id));
        await super.saveOrUpdatePlayers(localTeam.id, toSave);
    }

    async getSeasonTeams(seasonId, externalTeamIds) {
        const teams = await this.gamedayApiClient.getSeasonTeams(
            seasonId,
            externalTeamIds
        );

        return teams;
    }

    async getUserTeams(seasonId) {
        const teams = await this.gamedayApiClient.getUserTeams(seasonId);

        return teams;
    }

    async saveOrUpdateEvent(event) {
        const sanitizedEvent = sanitizeInvalidCharactersDeep(event);
        const remoteEvent = await this.gamedayApiClient.updateEvent(
            sanitizedEvent
        );

        return super.saveOrUpdateEvent(
            Object.assign(sanitizedEvent, remoteEvent)
        );
    }

    async getTimeZones() {
        let timezones = await super.getTimeZones();
        if (!timezones || !timezones.length) {
            timezones = await this.gamedayApiClient.getTimeZones();
            let appConfig = await applicationRepository.getAll().first();
            if (!appConfig) {
                appConfig = new ApplicationConfig();
            }
            appConfig["timeZones"] = timezones;
            await applicationRepository.put(appConfig);
        }

        return timezones;
    }

    async getEventsByDateSportCodeAndStatusCode(date, sportCode, statusCodes) {
        const events =
            await this.gamedayApiClient.getEventsByDateSportCodeAndStatusCode(
                date,
                sportCode,
                statusCodes
            );

        return this.mapEventsWithTeams(events);
    }

    async saveEventData(remoteEventId, eventData) {
        return this.gamedayApiClient.saveEventData(remoteEventId, eventData);
    }

    async fetchEventData(remoteEventId) {
        return this.gamedayApiClient.fetchEventData(remoteEventId);
    }

    async saveBoxscore(boxscore) {
        return this.gamedayApiClient.saveBoxscore(boxscore);
    }

    async getSeasonSettings(seasonId) {
        return this.gamedayApiClient.getSeasonSettings(seasonId);
    }

    async saveSeasonSettings(seasonId, settings) {
        return this.gamedayApiClient.saveSeasonSettings(seasonId, settings);
    }

    async getStreamSrcById(id) {
        return this.stretchLiveApiClient.getStreamSrcById(id);
    }

    _preserveLocalSeasonSettings(season, local) {
        if (local.ruleSetId) {
            season.ruleSetId = local.ruleSetId;
        }
        if (local.gameConfiguration) {
            season.gameConfiguration = local.gameConfiguration;
        }
        if (local.settingsUpdatedAt) {
            season.settingsUpdatedAt = local.settingsUpdatedAt;
        }
    }
}

export default RemoteDataStrategy;
